import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const BRAND = {
  primary: '#171363',
  accent: '#05998d',
  light: '#00aeef',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 60,
    paddingHorizontal: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#1f2937',
  },

  // Header: facility + INVOICE label/number
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  facilityBlock: { maxWidth: '60%' },
  facilityName: { fontSize: 16, fontWeight: 700, color: BRAND.primary, marginBottom: 4 },
  facilityLine: { fontSize: 9, color: '#4b5563', marginBottom: 1 },

  invoiceLabel: {
    fontSize: 22,
    fontWeight: 700,
    color: BRAND.accent,
    letterSpacing: 2,
    textAlign: 'right',
  },
  invoiceNumber: { fontSize: 11, color: BRAND.primary, textAlign: 'right', marginTop: 4, fontWeight: 700 },
  invoiceStatusPill: {
    marginTop: 6,
    alignSelf: 'flex-end',
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 3,
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 1,
  },

  // Divider
  divider: { height: 2, backgroundColor: BRAND.accent, marginBottom: 16 },
  dividerThin: { height: 1, backgroundColor: '#e5e7eb', marginVertical: 10 },

  // Bill-to + meta row
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  billToBlock: { flex: 1, paddingRight: 20 },
  metaBlock: { width: 200 },

  sectionLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: '#6b7280',
    letterSpacing: 1,
    marginBottom: 6,
  },
  billToName: { fontSize: 12, fontWeight: 700, color: BRAND.primary, marginBottom: 2 },
  billToLine: { fontSize: 9, color: '#374151', marginBottom: 1 },

  metaTable: { width: '100%' },
  metaRowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  metaKey: { fontSize: 9, color: '#6b7280' },
  metaVal: { fontSize: 9, fontWeight: 700, color: '#111827' },

  // Line item table
  table: { width: '100%', marginTop: 4 },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: BRAND.primary,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 2,
  },
  th: { fontSize: 8, fontWeight: 700, color: '#ffffff', letterSpacing: 0.5 },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 7,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  tableRowZebra: { backgroundColor: '#f9fafb' },
  td: { fontSize: 9, color: '#374151' },

  // Column widths
  colDate:    { width: '10%' },
  colMrn:     { width: '10%' },
  colService: { width: '19%' },
  colCpt:     { width: '9%' },
  colDesc:    { width: '23%' },
  colQty:     { width: '7%',  textAlign: 'right' },
  colRate:    { width: '10%', textAlign: 'right' },
  colTotal:   { width: '12%', textAlign: 'right' },

  // Totals
  totalsRow: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 14 },
  totalsBox: { width: 220 },
  totalLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e5e7eb',
  },
  totalLabel: { fontSize: 9, color: '#4b5563' },
  totalValue: { fontSize: 10, fontWeight: 700, color: '#111827' },
  balanceLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: BRAND.primary,
    marginTop: 6,
    borderRadius: 2,
  },
  balanceLabel: { fontSize: 10, color: '#ffffff', fontWeight: 700, letterSpacing: 0.5 },
  balanceValue: { fontSize: 12, color: '#ffffff', fontWeight: 700 },

  // Terms
  termsBlock: { marginTop: 28, padding: 12, backgroundColor: '#f9fafb', borderLeftWidth: 3, borderLeftColor: BRAND.accent },
  termsLabel: { fontSize: 8, fontWeight: 700, color: '#6b7280', letterSpacing: 1, marginBottom: 4 },
  termsText: { fontSize: 9, color: '#4b5563', lineHeight: 1.5 },

  // Footer (biller contact)
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  footerLabel: { fontSize: 8, fontWeight: 700, color: '#6b7280', letterSpacing: 1, marginBottom: 4 },
  footerLine: { fontSize: 8, color: '#6b7280', marginBottom: 1 },
  footerRow: { flexDirection: 'row', justifyContent: 'space-between' },
});

function fmtCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(val) || 0);
}

function fmtDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// On the printed invoice, the "draft" internal state is shown as OPEN — the
// PDF represents what the client will receive once sent, and clients should
// never see an internal "draft" marker.
function displayStatus(status) {
  return status === 'draft' ? 'open' : status;
}

function statusStyle(status) {
  const map = {
    open:    { backgroundColor: '#dbeafe', color: '#1e40af' },
    partial: { backgroundColor: '#fef3c7', color: '#92400e' },
    paid:    { backgroundColor: '#d1fae5', color: '#065f46' },
    void:    { backgroundColor: '#fee2e2', color: '#991b1b' },
  };
  return map[status] || map.open;
}

function InvoicePDF({ invoice, lineItems, client, billingSettings }) {
  const subtotal = lineItems.reduce((sum, li) => sum + Number(li.line_total || 0), 0);
  const paid = Number(invoice.amount_paid || 0);
  const balance = subtotal - paid;

  const facilityCityStateZip = [client.facility_city, client.facility_state, client.facility_zip]
    .filter(Boolean).join(client.facility_state ? ', ' : ' ').replace(', ' + client.facility_zip, ' ' + client.facility_zip);

  const clientCityStateZip = [client.city, client.state, client.zip]
    .filter(Boolean).join(client.state ? ', ' : ' ').replace(', ' + client.zip, ' ' + client.zip);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        {/* HEADER — Facility (left) + INVOICE label (right) */}
        <View style={styles.topRow}>
          <View style={styles.facilityBlock}>
            <Text style={styles.facilityName}>{client.facility_name}</Text>
            {client.facility_address && <Text style={styles.facilityLine}>{client.facility_address}</Text>}
            {facilityCityStateZip && <Text style={styles.facilityLine}>{facilityCityStateZip}</Text>}
            {client.facility_phone && <Text style={styles.facilityLine}>Phone: {client.facility_phone}</Text>}
            {client.facility_tax_id && <Text style={styles.facilityLine}>Tax ID: {client.facility_tax_id}</Text>}
            {client.facility_npi && <Text style={styles.facilityLine}>NPI: {client.facility_npi}</Text>}
          </View>
          <View>
            <Text style={styles.invoiceLabel}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>{invoice.invoice_number}</Text>
            <Text style={[styles.invoiceStatusPill, statusStyle(displayStatus(invoice.status))]}>
              {displayStatus(invoice.status || '').toUpperCase()}
            </Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* BILL-TO + INVOICE META */}
        <View style={styles.metaRow}>
          <View style={styles.billToBlock}>
            <Text style={styles.sectionLabel}>BILL TO</Text>
            <Text style={styles.billToName}>{client.name}</Text>
            {client.contact_name && <Text style={styles.billToLine}>Attn: {client.contact_name}</Text>}
            {client.address && <Text style={styles.billToLine}>{client.address}</Text>}
            {clientCityStateZip && <Text style={styles.billToLine}>{clientCityStateZip}</Text>}
            {client.contact_email && <Text style={styles.billToLine}>{client.contact_email}</Text>}
            {client.contact_phone && <Text style={styles.billToLine}>{client.contact_phone}</Text>}
          </View>
          <View style={styles.metaBlock}>
            <View style={styles.metaTable}>
              <View style={styles.metaRowItem}>
                <Text style={styles.metaKey}>Invoice Date</Text>
                <Text style={styles.metaVal}>{fmtDate(invoice.invoice_date)}</Text>
              </View>
              <View style={styles.metaRowItem}>
                <Text style={styles.metaKey}>Due Date</Text>
                <Text style={styles.metaVal}>{fmtDate(invoice.due_date)}</Text>
              </View>
              <View style={styles.metaRowItem}>
                <Text style={styles.metaKey}>Service Period</Text>
                <Text style={styles.metaVal}>{fmtDate(invoice.period_start)} – {fmtDate(invoice.period_end)}</Text>
              </View>
              <View style={styles.metaRowItem}>
                <Text style={styles.metaKey}>Payment Terms</Text>
                <Text style={styles.metaVal}>{client.payment_terms || 'Net 30'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* LINE ITEM TABLE */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.colDate]}>DATE</Text>
            <Text style={[styles.th, styles.colMrn]}>MRN</Text>
            <Text style={[styles.th, styles.colService]}>SERVICE</Text>
            <Text style={[styles.th, styles.colCpt]}>CPT</Text>
            <Text style={[styles.th, styles.colDesc]}>DESCRIPTION</Text>
            <Text style={[styles.th, styles.colQty]}>QTY</Text>
            <Text style={[styles.th, styles.colRate]}>RATE</Text>
            <Text style={[styles.th, styles.colTotal]}>AMOUNT</Text>
          </View>
          {lineItems.map((li, i) => (
            <View key={li.line_item_id} style={[styles.tableRow, i % 2 === 1 && styles.tableRowZebra]}>
              <Text style={[styles.td, styles.colDate]}>{fmtDate(li.service_date)}</Text>
              <Text style={[styles.td, styles.colMrn]}>{li.mrn || 'N/A'}</Text>
              <Text style={[styles.td, styles.colService, { fontWeight: 700, color: BRAND.primary }]}>{li.service_name}</Text>
              <Text style={[styles.td, styles.colCpt]}>{li.cpt_code || '—'}</Text>
              <Text style={[styles.td, styles.colDesc]}>{li.description || '—'}</Text>
              <Text style={[styles.td, styles.colQty]}>{li.quantity}</Text>
              <Text style={[styles.td, styles.colRate]}>{fmtCurrency(li.unit_rate)}</Text>
              <Text style={[styles.td, styles.colTotal, { fontWeight: 700 }]}>{fmtCurrency(li.line_total)}</Text>
            </View>
          ))}
        </View>

        {/* TOTALS */}
        <View style={styles.totalsRow}>
          <View style={styles.totalsBox}>
            <View style={styles.totalLine}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>{fmtCurrency(subtotal)}</Text>
            </View>
            {paid > 0 && (
              <View style={styles.totalLine}>
                <Text style={styles.totalLabel}>Amount Paid</Text>
                <Text style={[styles.totalValue, { color: BRAND.accent }]}>-{fmtCurrency(paid)}</Text>
              </View>
            )}
            <View style={styles.balanceLine}>
              <Text style={styles.balanceLabel}>BALANCE DUE</Text>
              <Text style={styles.balanceValue}>{fmtCurrency(balance)}</Text>
            </View>
          </View>
        </View>

        {/* TERMS */}
        {billingSettings?.default_terms && (
          <View style={styles.termsBlock}>
            <Text style={styles.termsLabel}>PAYMENT TERMS</Text>
            <Text style={styles.termsText}>{billingSettings.default_terms}</Text>
          </View>
        )}

        {/* FOOTER — Biller Contact */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerLabel}>QUESTIONS ABOUT THIS INVOICE?</Text>
          <View style={styles.footerRow}>
            <View>
              {billingSettings?.biller_name && <Text style={styles.footerLine}>{billingSettings.biller_name}</Text>}
              {billingSettings?.biller_phone && <Text style={styles.footerLine}>{billingSettings.biller_phone}</Text>}
              {billingSettings?.biller_email && <Text style={styles.footerLine}>{billingSettings.biller_email}</Text>}
            </View>
            <View>
              {billingSettings?.biller_address && <Text style={styles.footerLine}>{billingSettings.biller_address}</Text>}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default InvoicePDF;
